import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Tracks the main drawer independently from the native details state.
   * This lets a click during the closing animation reopen the drawer
   * immediately instead of being mistaken for another close request.
   *
   * @type {'closed' | 'opening' | 'open' | 'closing'}
   */
  #mainDrawerState = 'closed';

  /**
   * Every open or close action gets a new number. Animation callbacks only
   * run when their number is still current, so an old callback can never
   * reset or trap focus inside a drawer that has since changed state.
   *
   * @type {WeakMap<HTMLDetailsElement, number>}
   */
  #operationIds = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    const details = this.refs.details;

    this.#mainDrawerState =
      details.hasAttribute('open') &&
      details.classList.contains('menu-open')
        ? 'open'
        : 'closed';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open.
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    const isOpeningOrOpen =
      this.#mainDrawerState === 'opening' ||
      this.#mainDrawerState === 'open';

    return isOpeningOrOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer.
   *
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    const operationId = this.#startOperation(details);

    if (details === this.refs.details) {
      this.#mainDrawerState = 'opening';
      details.setAttribute('open', '');
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      if (
        !this.#isCurrentOperation(details, operationId) ||
        !details.hasAttribute('open')
      ) {
        return;
      }

      details.classList.add('menu-open');

      if (details === this.refs.details) {
        this.#mainDrawerState = 'open';
      }

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => {
          if (
            !this.#isCurrentOperation(details, operationId) ||
            !details.hasAttribute('open') ||
            !details.classList.contains('menu-open')
          ) {
            return;
          }

          trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer.
   *
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const operationId = this.#startOperation(details);

    if (details === this.refs.details) {
      this.#mainDrawerState = 'closing';
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        if (
          !this.#isCurrentOperation(details, operationId) ||
          details.classList.contains('menu-open')
        ) {
          return;
        }

        reset(details);

        if (details === this.refs.details) {
          this.#mainDrawerState = 'closed';
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Start a new open or close operation for a details element.
   *
   * @param {HTMLDetailsElement} details
   * @returns {number}
   */
  #startOperation(details) {
    const operationId =
      (this.#operationIds.get(details) ?? 0) + 1;

    this.#operationIds.set(details, operationId);

    return operationId;
  }

  /**
   * Check whether an animation callback still belongs to the latest action.
   *
   * @param {HTMLDetailsElement} details
   * @param {number} operationId
   * @returns {boolean}
   */
  #isCurrentOperation(details, operationId) {
    return this.#operationIds.get(details) === operationId;
  }

  /**
   * Attach animation-end listeners to animated menu elements.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  /**
   * Temporarily disable accordion animations when the drawer opens.
   *
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}